export const RESUMABLE_EEG_UPLOAD_SESSIONS_STORAGE_KEY =
  "eeg_resumable_upload_sessions_v1";

const MAX_PERSISTED_SESSIONS = 8;

export interface PersistedResumableEegUploadSession {
  uploadId: string;
  recordingId?: string;
  filename: string;
  filesize: number;
  studyId: string;
  chunkSize: number;
  totalChunks: number;
  patientId?: string;
  owningTeamId?: string | null;
  importTaskId?: string;
  pausedUntil?: number | null;
  startedAt: number;
  updatedAt: number;
}

export function loadPersistedResumableUploadSessions(): PersistedResumableEegUploadSession[] {
  if (typeof window === "undefined") return [];
  try {
    const raw =
      window.localStorage.getItem(RESUMABLE_EEG_UPLOAD_SESSIONS_STORAGE_KEY) ||
      window.sessionStorage.getItem(
      RESUMABLE_EEG_UPLOAD_SESSIONS_STORAGE_KEY,
      );
    if (!raw) return [];
    const parsed = JSON.parse(raw) as PersistedResumableEegUploadSession[];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (session) =>
        session?.uploadId &&
        session.filename &&
        Number.isFinite(session.filesize) &&
        session.filesize > 0 &&
        session.studyId &&
        Number.isFinite(session.chunkSize) &&
        session.chunkSize > 0 &&
        Number.isFinite(session.totalChunks) &&
        session.totalChunks > 0,
    );
  } catch {
    return [];
  }
}

export function savePersistedResumableUploadSessions(
  sessions: PersistedResumableEegUploadSession[],
): void {
  if (typeof window === "undefined") return;
  try {
    const trimmed = sessions
      .sort((left, right) => right.updatedAt - left.updatedAt)
      .slice(0, MAX_PERSISTED_SESSIONS);
    window.sessionStorage.setItem(
      RESUMABLE_EEG_UPLOAD_SESSIONS_STORAGE_KEY,
      JSON.stringify(trimmed),
    );
    window.localStorage.setItem(
      RESUMABLE_EEG_UPLOAD_SESSIONS_STORAGE_KEY,
      JSON.stringify(trimmed),
    );
  } catch {
    // Ignore quota / privacy mode failures.
  }
}

export function upsertPersistedResumableUploadSession(
  session: PersistedResumableEegUploadSession,
): void {
  const existing = loadPersistedResumableUploadSessions();
  const index = existing.findIndex((row) => row.uploadId === session.uploadId);
  const next = [...existing];
  if (index >= 0) {
    next[index] = { ...next[index], ...session, updatedAt: Date.now() };
  } else {
    next.unshift({ ...session, updatedAt: Date.now() });
  }
  savePersistedResumableUploadSessions(next);
}

export function removePersistedResumableUploadSession(uploadId: string): void {
  savePersistedResumableUploadSessions(
    loadPersistedResumableUploadSessions().filter(
      (session) => session.uploadId !== uploadId,
    ),
  );
}

/** Drop orphaned sessions for the same import task / file before a new init. */
export function removePersistedResumableUploadSessionsMatching(match: {
  importTaskId?: string;
  filename?: string;
  studyId?: string;
  exceptUploadId?: string;
}): void {
  savePersistedResumableUploadSessions(
    loadPersistedResumableUploadSessions().filter((session) => {
      if (
        match.exceptUploadId &&
        session.uploadId === match.exceptUploadId
      ) {
        return true;
      }
      if (
        match.importTaskId &&
        session.importTaskId &&
        session.importTaskId === match.importTaskId
      ) {
        return false;
      }
      if (
        match.filename &&
        match.studyId &&
        session.filename === match.filename &&
        session.studyId === match.studyId
      ) {
        return false;
      }
      return true;
    }),
  );
}

export function clearPersistedResumableUploadSessions(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(RESUMABLE_EEG_UPLOAD_SESSIONS_STORAGE_KEY);
    window.localStorage.removeItem(RESUMABLE_EEG_UPLOAD_SESSIONS_STORAGE_KEY);
  } catch {
    // Ignore storage failures.
  }
}

export function pausePersistedResumableUploadSession(
  uploadId: string,
  pausedUntil: number | null | undefined,
): void {
  const sessions = loadPersistedResumableUploadSessions();
  savePersistedResumableUploadSessions(
    sessions.map((session) =>
      session.uploadId === uploadId
        ? { ...session, pausedUntil, updatedAt: Date.now() }
        : session,
    ),
  );
}

export function getPersistedResumableUploadPauseUntil(
  uploadId: string,
): number | null | undefined {
  return loadPersistedResumableUploadSessions().find(
    (session) => session.uploadId === uploadId,
  )?.pausedUntil;
}

export function fileMatchesResumableSession(
  file: File,
  session: Pick<PersistedResumableEegUploadSession, "filename" | "filesize">,
): boolean {
  return file.name === session.filename && file.size === session.filesize;
}
